<template>
  <div class="fixed inset-0 bg-gray-900/50 flex justify-center items-center p-4 z-50">
    <div class="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-800">Create New User</h2>
        <button
          type="button"
          class="text-gray-600 hover:text-gray-800 text-2xl leading-none"
          @click="closeForm"
        >
          &times;
        </button>
      </div>

      <!-- Success State -->
      <div v-if="successMessage">
        <div class="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p class="sensitive text-sm text-green-600" data-demo-blur>{{ successMessage }}</p>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            @click="resetForm"
          >
            Create Another
          </button>
          <button
            type="button"
            class="btn-primary"
            @click="closeForm"
          >
            Done
          </button>
        </div>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="submitForm">
        <div class="mb-4">
          <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
            Email <span class="text-red-500">*</span>
          </label>
          <input
            id="email"
            v-model="formData.email"
            type="email"
            class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
            placeholder="Enter email"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label for="first_name" class="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              id="first_name"
              v-model="formData.first_name"
              type="text"
              class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              placeholder="First name"
            />
          </div>

          <div>
            <label for="last_name" class="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              id="last_name"
              v-model="formData.last_name"
              type="text"
              class="sensitive w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
              placeholder="Last name"
            />
          </div>
        </div>

        <div class="mb-4 p-4 bg-gray-50 rounded-md">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Access Roles (select at least one)
          </label>
          <div class="space-y-2 max-h-48 overflow-y-auto">
            <div v-for="role in availableAccessRoles" :key="role.id" class="flex items-center">
              <input
                :id="`role_${role.id}`"
                v-model="formData.access_roles"
                type="checkbox"
                :value="role.id"
                class="h-4 w-4 text-blue-600 focus:ring-blue-dark border-gray-300 rounded"
              />
              <label :for="`role_${role.id}`" class="ml-2 text-sm text-gray-700">
                {{ role.name }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p class="text-sm text-red-600">{{ errorMessage }}</p>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            type="button"
            @click="closeForm"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Creating...' : 'Create User' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { createUser } from '@/services/api/adminUsers';
import { mapGetters } from 'vuex';

export default {
  name: 'CreateUserForm',
  data() {
    return {
      formData: {
        email: '',
        first_name: '',
        last_name: '',
        access_roles: [],
      },
      isSubmitting: false,
      errorMessage: '',
      successMessage: '',
    };
  },
  computed: {
    ...mapGetters('featureConfig', ['roles']),
    availableAccessRoles() {
      return this.roles
        .filter(r => !r.hide_from_config)
        .map(r => ({ id: r.role_id, name: r.display_name }));
    },
  },
  mounted() {
    this.$store.dispatch('featureConfig/fetchConfig');
  },
  methods: {
    async submitForm() {
      this.errorMessage = '';
      this.successMessage = '';
      this.isSubmitting = true;

      try {
        if (!this.formData.email.trim()) {
          this.errorMessage = 'Email is required.';
          return;
        }
        if (!this.formData.access_roles.length) {
          this.errorMessage = 'At least one access role is required.';
          return;
        }
        const payload = {
          ...this.formData,
          email: this.formData.email.trim(),
        };
        const result = await createUser(payload);
        const email = this.formData.email.trim();
        const loginUrl = `${window.location.origin}/login`;
        this.successMessage = result.login_method === 'sso'
          ? `Account created for ${email}. The user can sign in via Microsoft SSO.`
          : `Account created for ${email}. Have the user go to ${loginUrl}, enter their email, and click Reset Password to set their password.`;
        this.$emit('user-created');
      } catch (error) {
        console.error('Error creating user:', error);
        if (error.response?.data) {
          // Handle validation errors from backend
          if (typeof error.response.data === 'string') {
            this.errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            this.errorMessage = error.response.data.detail;
          } else {
            // Collect all field errors
            const errors = Object.entries(error.response.data)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('; ');
            this.errorMessage = errors || 'Failed to create user';
          }
        } else {
          this.errorMessage = 'Failed to create user. Please try again.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.formData = { email: '', first_name: '', last_name: '', access_roles: [] };
      this.successMessage = '';
      this.errorMessage = '';
    },
    closeForm() {
      this.$emit('close');
    },
  },
};
</script>
